import React, { useState, useEffect } from 'react';
import { isMenuAllowed, resolveTeacherPlan } from '../../config/teacherPlans';
import Logo from './Logo';
import ToggleButtons from './ToggleButtons';
import MainMenu from './MainMenu';
import RightIcons from './RightIcons';
import { User } from '../../utils/firebase';
import { isTeacher, isSuperAdmin } from '../../utils/firebase';
import { isNativeMenuVisible } from '../../config/features';


// 네비게이션 타입 정의
type NavbarType = 'teacher-class' | 'student-class' | 'subject' | 'notes' | 'notes-student' | 'notes-teacher' | 'class-materials-manage' | 'teacher-training-manage' | 'tools' | 'admin' | 'ib-grading' | 'minecraft-education' | 'public' | 'makecode-arcade' | 'info-chief';

// 메뉴 아이템 인터페이스
interface MenuItem {
  id: string;
  title: string;
  hasIcon?: boolean;
  path?: string;
  subItems?: MenuItem[];
  teacherOnly?: boolean; // 교사 전용 메뉴 여부
}

// SmartNavbar props 인터페이스
interface SmartNavbarProps {
  type: NavbarType;                    // 네비게이션 타입
  selectedToggleIndex: number;          // 현재 선택된 토글 인덱스
  selectedMenuId?: string;             // 현재 선택된 메뉴 ID
  onToggleChange: (index: number) => void; // 토글 변경 시 호출되는 함수
  onMenuClick: (menuId: string, path?: string) => void;   // 메뉴 클릭 시 호출되는 함수
  onChatClick?: () => void;            // 채팅 클릭 시 실행할 함수
  onNotificationClick?: () => void;    // 알림 클릭 시 실행할 함수
  onUserClick?: (event?: React.MouseEvent<HTMLElement>) => void;            // 사용자 클릭 시 실행할 함수
  onLogoClick?: () => void;            // 로고 클릭 시 실행할 함수
  className?: string;                  // 추가 CSS 클래스
  unreadNotificationCount?: number;    // 읽지 않은 알림 개수
  currentUser?: User | null;            // 현재 사용자 정보 (교사 전용 메뉴 필터링용)
}

// SmartNavbar 컴포넌트 정의
const SmartNavbar: React.FC<SmartNavbarProps> = ({ 
  type,                    // 네비게이션 타입
  selectedToggleIndex,     // 현재 선택된 토글 인덱스
  selectedMenuId,          // 현재 선택된 메뉴 ID
  onToggleChange,          // 토글 변경 함수
  onMenuClick,             // 메뉴 클릭 함수
  onChatClick,             // 채팅 클릭 함수
  onNotificationClick,     // 알림 클릭 함수
  onUserClick,             // 사용자 클릭 함수
  onLogoClick,             // 로고 클릭 함수
  className = '',
  unreadNotificationCount = 0,
  currentUser = null       // 현재 사용자 정보
}) => {
  // 네비게이션 타입에 따른 설정
  const getNavbarConfig = () => {
    switch (type) {
      case 'teacher-class':
        return {
          logo: 'MARU',
          toggles: ['학급운영', '교과운영'],
          menus: [
            { 
              id: 'dashboard', 
              title: '대시보드', 
              hasIcon: false,
              path: '/class/teacher/ClassTeacherHome'
            },
            { 
              id: 'management', 
              title: '관리', 
              hasIcon: true,
              path: '/class/teacher/ClassManagement',
              subItems: [
                { id: 'class-management', title: '학급 관리', hasIcon: false, path: '/class/teacher/ClassManagement' },
                { id: 'seating-management', title: '자리 관리', hasIcon: false, path: '/class/teacher/SeatManagement' },
                { id: 'attendance-management', title: '출결 관리', hasIcon: false, path: '/class/teacher/AttendanceManagement' },
                { id: 'role-management', title: '학급 역할', hasIcon: false, path: '/class/teacher/ClassRoleManagement' },
                { id: 'vote-management', title: '투표', hasIcon: false, path: '/class/teacher/VoteManagement' },
                { id: 'achievement-management', title: '업적', hasIcon: false, path: '/class/teacher/AchievementManagement' },
                { id: 'virtual-pet-management', title: '가상펫 관리', hasIcon: false, path: '/class/teacher/VirtualPetManagement' },
                { id: 'myhome-moderation', title: '마이홈 방명록', hasIcon: false, path: '/class/teacher/MyHomeModeration' },
                { id: 'manito', title: '마니또', hasIcon: false, path: '/class/teacher/ManitoManagement' },
                { id: 'birthday-management', title: '생일 관리', hasIcon: false, path: '/class/teacher/BirthdayManagement' }
              ]
            },
            { 
              id: 'operation', 
              title: '운영', 
              hasIcon: true,
              path: '/class/teacher/operation',
              subItems: [
                { id: 'class-materials', title: '학급 자료', hasIcon: false, path: '/class/teacher/ClassMaterials' },
                { id: 'daily-mission', title: '데일리 미션', hasIcon: false, path: '/class/teacher/DailyMission' },
                { id: 'role-auction', title: '역할 경매', hasIcon: false, path: '/class/teacher/RoleAuctionManagement' },
                { id: 'lottery', title: '뽑기', hasIcon: false, path: '/class/teacher/Lottery' }
              ]
            },
            { 
              id: 'points', 
              title: '포인트', 
              hasIcon: true,
              path: '/class/teacher/PointApprovalManagement',
              subItems: [
                { id: 'daily-points', title: '일별 포인트 지급', hasIcon: false, path: '/class/teacher/point-approval/daily' },
                { id: 'weekly-points', title: '주별 포인트 지급', hasIcon: false, path: '/class/teacher/point-approval/weekly' },
                { id: 'monthly-points', title: '월별 포인트 지급', hasIcon: false, path: '/class/teacher/point-approval/monthly' },
                { id: 'salary-tax', title: '급여 세금(공제)', hasIcon: false, path: '/class/teacher/point-approval/tax' },
                { id: 'special-points', title: '특별 포인트 지급', hasIcon: false, path: '/class/teacher/special-point-approval' },
                { id: 'class-points', title: '학급 포인트', hasIcon: false, path: '/class/teacher/ClassPointManagement' }
              ]
            },
            { 
              id: 'shop', 
              title: '상점', 
              hasIcon: true,
              path: '/class/teacher/StoreManagement',
              subItems: [
                { id: 'store-management', title: '상점 관리', hasIcon: false, path: '/class/teacher/StoreManagement' },
                { id: 'item-approval', title: '아이템 승인', hasIcon: false, path: '/class/teacher/ItemApproval' }
              ]
            },
            { 
              id: 'schedule', 
              title: '일정', 
              hasIcon: false,
              path: '/class/teacher/ScheduleManagement'
            },
            {
              id: 'messages',
              title: '메시지함',
              hasIcon: false,
              path: '/class/teacher/MessageInbox'
            },
            { 
              id: 'settings', 
              title: '설정', 
              hasIcon: false,
              path: '/class/teacher/TeacherSetting'
            }
          ]
        };
      
      case 'student-class':
        return {
          logo: 'MARU',
          toggles: ['학급운영', '교과운영'],
          menus: [
            { 
              id: 'dashboard', 
              title: '홈', 
              hasIcon: false,
              path: '/class/student'
            },
            {
              id: 'my-home',
              title: '마이홈',
              hasIcon: false,
              path: '/class/student/MyHome'
            },
            { 
              id: 'class-activities', 
              title: '학급 활동', 
              hasIcon: true,
              path: '/class/student/activities',
              subItems: [
                { id: 'vote', title: '투표', hasIcon: false, path: '/class/student/Vote' },
                { id: 'role-auction', title: '역할 경매', hasIcon: false, path: '/class/student/RoleAuction' },
                { id: 'seat-reservation', title: '좌석 예약', hasIcon: false, path: '/class/student/SeatReservation' },
                { id: 'virtual-pet', title: '가상 펫', hasIcon: false, path: '/class/student/VirtualPet' },
                { id: 'character-wardrobe', title: '캐릭터 옷장', hasIcon: false, path: '/class/student/CharacterWardrobe' },
                { id: 'manito', title: '마니또', hasIcon: false, path: '/class/student/Manito' },
                { id: 'birthday-rolling-paper', title: '생일 롤링페이퍼', hasIcon: false, path: '/class/student/BirthdayRollingPaper' }
              ]
            },
            { 
              id: 'points', 
              title: '내 포인트', 
              hasIcon: false,
              path: '/class/student/StudentClassPoints'
            },
            { 
              id: 'achievements', 
              title: '업적', 
              hasIcon: false,
              path: '/class/student/StudentAchievements'
            },
            { 
              id: 'shop', 
              title: '상점', 
              hasIcon: false,
              path: '/class/student/ClassItemShop'
            },
            { 
              id: 'items', 
              title: '내 아이템', 
              hasIcon: false,
              path: '/class/student/ClassMyItems'
            },
            {
              id: 'messages',
              title: '메시지함',
              hasIcon: false,
              path: '/class/student/MessageInbox'
            },
            { 
              id: 'settings', 
              title: '설정', 
              hasIcon: false,
              path: '/class/student/StudentSettings'
            }
          ]
        };
      
      case 'subject':
        return {
          logo: 'MARU',
          toggles: ['학급운영', '교과운영'],
          menus: [
            { 
              id: 'dashboard', 
              title: '홈', 
              hasIcon: false,
              path: '/subject/teacher/SubjectTeacherHome'
            },
            { 
              id: 'subject', 
              title: '교과', 
              hasIcon: true,
              path: '/subject/teacher/SubjectTeacherPage',
              subItems: [
                { id: 'class-management', title: '교실 관리', hasIcon: false, path: '/subject/teacher/ClassManagement' },
                { id: 'lesson-management', title: '수업 관리', hasIcon: false, path: '/subject/teacher/LessonManagement' },
                { id: 'vote-management', title: '투표', hasIcon: false, path: '/subject/teacher/VoteManagement' },
                { id: 'achievement-management', title: '업적', hasIcon: false, path: '/subject/teacher/AchievementManagement' }
              ]
            },
            { 
              id: 'education', 
              title: '교육', 
              hasIcon: false,
              path: '/subject/teacher/education'
            },
            { 
              id: 'stamp', 
              title: '도장', 
              hasIcon: true,
              path: '/subject/teacher/stamp'
            },
            { 
              id: 'shop', 
              title: '상점', 
              hasIcon: false,
              path: '/subject/teacher/shop'
            },
            { 
              id: 'schedule', 
              title: '일정', 
              hasIcon: false,
              path: '/subject/teacher/schedule'
            },
            { 
              id: 'settings', 
              title: '설정', 
              hasIcon: false,
              path: '/subject/teacher/settings'
            }
          ]
        };
      
      case 'notes-student':
        return {
          logo: 'MARU',
          toggles: [],
          menus: [
            {
              id: 'materials',
              title: '수업 자료',
              hasIcon: false,
              path: '/class-materials/materials',
            },
            {
              id: 'classroom',
              title: '수업 자료실',
              hasIcon: false,
              path: '/class-materials/classroom',
            },
            {
              id: 'ai-robot',
              title: 'AI 로봇 프로젝트',
              hasIcon: false,
              path: '/class-materials/ai-robot-project',
            },
            {
              id: 'my-manage',
              title: '내 자료 관리',
              hasIcon: false,
              path: '/class-materials/manage',
              teacherOnly: true,
            },
          ],
        };

      case 'notes-teacher':
        return {
          logo: 'MARU',
          toggles: [],
          menus: [
            {
              id: 'training',
              title: '메인',
              hasIcon: false,
              path: '/teacher-training',
            },
            {
              id: 'lobby',
              title: '로비',
              hasIcon: false,
              path: '/teacher-training/my',
              teacherOnly: true,
            },
            {
              id: 'my-manage',
              title: '연수 관리',
              hasIcon: false,
              path: '/teacher-training/manage',
              teacherOnly: true,
            },
          ],
        };

      case 'class-materials-manage':
        return {
          logo: 'MARU',
          toggles: [],
          menus: [
            {
              id: 'manage-home',
              title: '내 자료 관리',
              hasIcon: false,
              path: '/class-materials/manage',
            },
            {
              id: 'public-view',
              title: '공개 페이지',
              hasIcon: false,
              path: '/class-materials/materials',
            },
          ],
        };

      case 'teacher-training-manage':
        return {
          logo: 'MARU',
          toggles: [],
          menus: [
            {
              id: 'training',
              title: '메인',
              hasIcon: false,
              path: '/teacher-training',
            },
            {
              id: 'lobby',
              title: '로비',
              hasIcon: false,
              path: '/teacher-training/my',
            },
            {
              id: 'my-manage',
              title: '연수 관리',
              hasIcon: false,
              path: '/teacher-training/manage',
            },
          ],
        };
      
      case 'tools':
        return {
          logo: 'MARU',
          toggles: ['학급운영', '교과운영'],
          menus: [
            { id: 'dashboard', title: '대시보드', hasIcon: false },
            { id: 'tools', title: '도구', hasIcon: false },
            { id: 'utilities', title: '유틸리티', hasIcon: false }
          ]
        };
      
      case 'admin':
        return {
          logo: 'MARU',
          toggles: ['학급운영', '교과운영'],
          menus: [
            { id: 'dashboard', title: '대시보드', hasIcon: false },
            { id: 'users', title: '사용자 관리', hasIcon: false },
            { id: 'schools', title: '학교 관리', hasIcon: false },
            { id: 'settings', title: '설정', hasIcon: false }
          ]
        };
      
      case 'ib-grading':
        return {
          logo: 'MARU',
          toggles: [], // 토글 버튼 제거
          menus: [
            { 
              id: 'guide', 
              title: '홈', 
              hasIcon: false
            },
            { 
              id: 'subject-group', 
              title: '교과군별 성취수준 파일 사용법', 
              hasIcon: false,
              path: '/ib-grading/subject-group-guide'
            },
            { 
              id: 'final-grade', 
              title: '학년말 전체 파일 사용법', 
              hasIcon: false,
              path: '/ib-grading/final-grade-guide'
            },
            { 
              id: 'download', 
              title: '다운로드', 
              hasIcon: false
            },
            { 
              id: 'contact', 
              title: '문의', 
              hasIcon: false
            }
          ]
        };
      
      case 'minecraft-education':
        return {
          logo: 'MARU',
          toggles: [], // 토글 버튼 제거
          menus: [
            { 
              id: 'home', 
              title: '홈', 
              hasIcon: false,
              path: '/minecraft-education'
            },
            { 
              id: 'world-share', 
              title: '월드 공유', 
              hasIcon: false,
              path: '/minecraft-education/world-share'
            },
            { 
              id: 'mod-guide', 
              title: '모드 안내', 
              hasIcon: false,
              path: '/minecraft-education/mod-guide'
            },
            { 
              id: 'makecode', 
              title: 'Makecode', 
              hasIcon: false,
              path: '/minecraft-education/makecode'
            }
          ]
        };

      case 'public':
        return {
          logo: 'MARU',
          toggles: [],
          menus: [],
          showRightIcons: false
        };

      case 'makecode-arcade':
        return {
          logo: 'MARU',
          toggles: [],
          menus: [
            { 
              id: 'home', 
              title: '홈', 
              hasIcon: false,
              path: '/makecodearcade'
            },
            { 
              id: 'chart', 
              title: '게임 차트', 
              hasIcon: false,
              path: '/makecodearcade/chart'
            },
            { 
              id: 'store', 
              title: '게임 스토어', 
              hasIcon: false,
              path: '/makecodearcade/store'
            },
            { 
              id: 'gallery', 
              title: '갤러리', 
              hasIcon: false,
              path: '/makecodearcade/gallery'
            },
            { 
              id: 'explains', 
              title: '확장 블록 설명', 
              hasIcon: false,
              path: '/makecodearcade/explains'
            },
            { 
              id: 'extensions', 
              title: '추가 기능', 
              hasIcon: false,
              path: '/makecodearcade/extensions'
            },
            { 
              id: 'register', 
              title: '게임 등록', 
              hasIcon: true,
              path: '/makecodearcade/register',
              subItems: [
                { id: 'register-new', title: '게임 등록', hasIcon: false, path: '/makecodearcade/register' },
                { id: 'my-games', title: '내가 등록한 게임', hasIcon: false, path: '/makecodearcade/register/my-games' },
                { id: 'register-guide', title: '등록 가이드', hasIcon: false, path: '/makecodearcade/register-guide' },
              ],
            },
            { 
              id: 'class-management', 
              title: '수업 관리', 
              hasIcon: true,
              path: '/makecodearcade/class-management',
              teacherOnly: true,  // 교사 전용 메뉴
              subItems: [
                { id: 'game-management', title: '학급 게임 관리', hasIcon: false, path: '/makecodearcade/game-management' },
                { id: 'student-management', title: '학생 관리', hasIcon: false, path: '/makecodearcade/student-management' },
                { id: 'upload-code', title: '등록 코드', hasIcon: false, path: '/makecodearcade/upload-code' },
                { id: 'evaluation-code', title: '평가 코드 생성', hasIcon: false, path: '/makecodearcade/evaluation-code' }
              ]
            }
          ]
        };

      case 'info-chief':
        return {
          logo: '정보부장 플랫폼',
          toggles: [],
          menus: [
            {
              id: 'info-chief-home',
              title: '홈',
              hasIcon: false,
              path: '/info-chief'
            },
            {
              id: 'info-category',
              title: '정보관련',
              hasIcon: false,
              path: '/info-chief/info-category'
            },
            {
              id: 'privacy-category',
              title: '개인정보 관련',
              hasIcon: false,
              path: '/info-chief/privacy-category'
            },
            {
              id: 'info-chief-devices',
              title: '디지털 기기 관련',
              hasIcon: false,
              path: '/info-chief/device-materials'
            },
            {
              id: 'info-chief-url-access-list',
              title: '화이트리스트/블랙리스트',
              hasIcon: false,
              path: '/info-chief/url-access-list'
            },
            {
              id: 'info-chief-related-sites',
              title: '관련 사이트',
              hasIcon: false,
              path: '/info-chief/related-sites'
            }
          ]
        };
      
      default:
        return {
          logo: 'MARU',
          toggles: ['학급운영', '교과운영'],
          menus: []
        };
    }
  };

  const config = getNavbarConfig();
  
  // 교사 전용 메뉴, 네이티브 앱에서 뺀 기능 필터링
  const teacherPlan = resolveTeacherPlan(currentUser?.plan, currentUser?.role);
  const filteredMenus = (config.menus as MenuItem[])
    .filter((menu) => {
      if (!isNativeMenuVisible(menu.id)) return false;
      if (menu.teacherOnly) {
        return (
          !!currentUser &&
          (isTeacher(currentUser) ||
            isSuperAdmin(currentUser) ||
            currentUser.role === 'schoolAdmin')
        );
      }
      return true;
    })
    .map((menu) => {
      if (!menu.subItems?.length) return menu;
      return {
        ...menu,
        subItems: menu.subItems.filter(
          (subItem) => isNativeMenuVisible(subItem.id) && isMenuAllowed(teacherPlan, subItem.id, type)
        ),
      };
    })
    .filter((menu) => {
      if (menu.subItems) return menu.subItems.length > 0;
      return isMenuAllowed(teacherPlan, menu.id, type);
    })
    .filter((menu) => !menu.subItems || menu.subItems.length > 0);
  
  // 필터링된 메뉴로 config 업데이트
  const finalConfig = {
    ...config,
    menus: filteredMenus
  };
  
  // 모바일 감지 및 햄버거 메뉴 상태
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMenuClick = (itemId: string, path?: string) => {
    const item = finalConfig.menus.find(menu => menu.id === itemId) as MenuItem | undefined;
    if (item && 'subItems' in item && item.subItems && item.subItems.length > 0) {
      // 하위메뉴가 있는 경우 하위메뉴 토글
      setOpenSubmenu(openSubmenu === itemId ? null : itemId);
    } else {
      // 하위메뉴가 없는 경우 직접 페이지 이동
      onMenuClick(itemId, path);
      setDrawerOpen(false);
      setOpenSubmenu(null);
    }
  };

  const handleSubmenuClick = (subItem: MenuItem) => {
    onMenuClick(subItem.id, 'path' in subItem ? subItem.path : undefined);
    setDrawerOpen(false);
    setOpenSubmenu(null);
  };

  if (type === 'public') {
    return (
      <div
        data-layout-navbar
        style={{
          boxSizing: 'border-box',
          width: '100%',
          maxWidth: '1440px',
          height: '60px',
          paddingLeft: '24px',
          paddingRight: '24px',
          display: 'flex',
          alignItems: 'center',
          background: 'transparent'
        }}
      >
        <Logo text={finalConfig.logo} onClick={onLogoClick} />
      </div>
    );
  }

  return (
    <>
      <div data-layout-navbar
        style={{
          boxSizing: 'border-box',
          width: type === 'ib-grading' ? '100%' : (isMobile ? '100%' : '1440px'),
          height: '60px',
          paddingLeft: isMobile ? '20px' : '24px',
          paddingRight: isMobile ? '20px' : '24px',
          position: type === 'ib-grading' ? 'fixed' : 'absolute',
          top: type === 'ib-grading' ? '0' : undefined,
          left: type === 'ib-grading' ? '0' : undefined,
          zIndex: type === 'ib-grading' ? 1000 : 500,
          justifyContent: 'center',
          alignItems: 'center',
          gap: isMobile ? '12px' : '60px',
          display: type === 'ib-grading' ? 'flex' : (isMobile ? 'flex' : 'inline-flex'),
          flexWrap: 'nowrap',
          background: 'transparent'
        }}>
          <div style={{
            width: isMobile ? undefined : '1120px',
            flex: isMobile ? '1 1 0%' : undefined,
            minWidth: isMobile ? 0 : undefined,
            maxWidth: isMobile ? 'calc(100% - 108px)' : undefined,
            justifyContent: 'space-between',
            alignItems: 'center',
            display: 'flex'
          }}>
            <div style={{
              justifyContent: 'center',
              alignItems: 'center',
              gap: isMobile ? '16px' : '40px',
              display: 'flex'
            }}>
              {/* 모바일 햄버거 메뉴 버튼 */}
              {isMobile && (
                <div
                  onClick={() => setDrawerOpen(true)}
                  style={{
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 12H21M3 6H21M3 18H21"
                      stroke={type === 'makecode-arcade' ? '#F4F4F8' : '#293044'}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
              
              <Logo text={finalConfig.logo} onClick={onLogoClick} />
              {!isMobile && finalConfig.toggles.length > 0 && (
                <ToggleButtons
                  options={finalConfig.toggles}
                  selectedIndex={selectedToggleIndex}
                  onToggle={onToggleChange}
                />
              )}
            </div>
          {/* 메인 메뉴 - 데스크톱에서만 표시 */}
          {!isMobile && (
            <MainMenu 
              items={finalConfig.menus}
              selectedItemId={selectedMenuId}
              onMenuClick={onMenuClick}
              textColor={type === 'makecode-arcade' ? '#E8E8F2' : undefined}
            />
          )}
        </div>
        
        {/* 우측 아이콘들 */}
        {(finalConfig as { showRightIcons?: boolean }).showRightIcons !== false && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        <RightIcons 
          onChatClick={onChatClick}
          onNotificationClick={onNotificationClick}
          onUserClick={onUserClick}
            unreadNotificationCount={unreadNotificationCount}
            light={type === 'makecode-arcade'}
        />
        </div>
        )}
      </div>

      {/* 모바일 드로어 메뉴 */}
      {isMobile && (
        <>
          {/* 오버레이 */}
          {drawerOpen && (
            <div
              onClick={() => setDrawerOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999,
                transition: 'opacity 0.3s ease'
              }}
            />
          )}
          
          {/* 드로어 */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: drawerOpen ? 0 : '-280px',
              width: '280px',
              height: '100vh',
              background: 'white',
              boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              transition: 'left 0.3s ease',
              overflowY: 'auto',
              paddingTop: '20px',
              paddingBottom: '20px'
            }}
          >
            {/* 드로어 헤더 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 20px 20px 20px',
              borderBottom: '1px solid #F0F0F0'
            }}>
              <Logo text={finalConfig.logo} onClick={onLogoClick} />
              <div
                onClick={() => setDrawerOpen(false)}
                style={{
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="#293044"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            {/* 토글 버튼들 */}
            {finalConfig.toggles.length > 0 && (
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #F0F0F0'
              }}>
                <ToggleButtons
                  options={finalConfig.toggles}
                  selectedIndex={selectedToggleIndex}
                  onToggle={onToggleChange}
                />
              </div>
            )}

            {/* 메뉴 아이템들 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '8px 0'
            }}>
              {finalConfig.menus.map((item) => {
                const menuItem = item as MenuItem;
                const hasSubItems = 'subItems' in menuItem && menuItem.subItems && menuItem.subItems.length > 0;
                const isSelected = menuItem.id === selectedMenuId
                  || (hasSubItems && menuItem.subItems?.some((sub) => sub.id === selectedMenuId));
                const isSubmenuOpen = openSubmenu === menuItem.id;
                
                return (
                  <div key={menuItem.id}>
                    <div
                      onClick={() => handleMenuClick(menuItem.id, 'path' in menuItem ? menuItem.path : undefined)}
                      style={{
                        padding: '16px 20px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#FFF4ED' : 'transparent',
                        borderLeft: isSelected ? '3px solid #FF6F0F' : '3px solid transparent',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = '#F8F8F8';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div style={{
                        color: isSelected ? '#FF6F0F' : '#293044',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: isSelected ? '600' : '500',
                        lineHeight: '21px'
                      }}>
                        {menuItem.title}
                      </div>
                      {hasSubItems && (
                        <div style={{
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transform: isSubmenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease'
                        }}>
                          <img 
                            src="/images/icons/name=down.svg" 
                            alt="화살표" 
                            style={{
                              width: '16px',
                              height: '16px'
                            }}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* 하위메뉴 */}
                    {hasSubItems && isSubmenuOpen && 'subItems' in menuItem && menuItem.subItems && (
                      <div style={{
                        backgroundColor: '#F8F8F8',
                        paddingLeft: '20px'
                      }}>
                        {menuItem.subItems.map((subItem: MenuItem) => {
                          const isSubSelected = subItem.id === selectedMenuId;
                          return (
                          <div
                            key={subItem.id}
                            onClick={() => handleSubmenuClick(subItem)}
                            style={{
                              padding: '12px 20px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: isSubSelected ? '600' : '400',
                              color: isSubSelected ? '#FF6F0F' : '#293044',
                              backgroundColor: isSubSelected ? '#FFF4ED' : 'transparent',
                              lineHeight: '21px',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubSelected) {
                                e.currentTarget.style.backgroundColor = '#F0F0F0';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubSelected) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {subItem.title}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 모바일: 프로필 메뉴 (상단 아이콘이 밀릴 때 대비) */}
            {onUserClick && (
              <div
                style={{
                  marginTop: '24px',
                  padding: '16px 20px 24px',
                  borderTop: '1px solid var(--Color-line-normal, #F0F0F0)'
                }}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    onUserClick(e);
                    setDrawerOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    border: '1px solid var(--Color-line-normal, #F0F0F0)',
                    background: 'var(--Color-background-sub, #F8F8F8)',
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    fontSize: '15px',
                    fontWeight: 600,
                    fontFamily: 'Pretendard, sans-serif',
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}
                >
                  프로필 · 계정 메뉴
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

// SmartNavbar 컴포넌트를 다른 파일에서 사용할 수 있도록 내보내기
export default SmartNavbar;
